import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { useDarkStyles, useDarkColor } from '../../../theme/useDarkStyles';

export interface KPIFilterDropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

// Tooltip shown on hover for option labels that are truncated with an ellipsis.
// Mirrors the header tooltip in HarborDataTable: portalled to document.body with a
// very high zIndex so it paints above the dropdown modal. Web-only by design.
const ItemTooltip: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
  text: string;
}> = ({ visible, position, text }) => {
  const dc = useDarkColor();
  if (Platform.OS !== 'web' || !visible || !position || !text) return null;
  const win: any = typeof globalThis !== 'undefined' ? (globalThis as any) : {};
  if (!win.document) return null;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left: position.x + 12,
        top: position.y - 8,
        maxWidth: 320,
        zIndex: 2147483647,
        pointerEvents: 'none' as any,
      }}
    >
      <View
        style={{
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 6,
          backgroundColor: dc('#1C1C1C', 'bg'),
        }}
      >
        <Text style={{ color: '#FFFFFF', fontSize: 12, fontWeight: '400', lineHeight: 16 }}>
          {text}
        </Text>
      </View>
    </View>
  );

  return createPortal(overlay, win.document.body);
};

interface KPIFilterDropdownProps {
  childList: KPIFilterDropdownItem[];
  selectedValue: KPIFilterDropdownItem[];
  setSelectedValue: (value: KPIFilterDropdownItem[]) => void;
  placeholder?: string;
  readOnly?: boolean;
  style?: any;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  singleSelect?: boolean;
  testID?: string;
  autoFocusSearchOnOpen?: boolean;
  sortItems?: boolean;
  disabledIds?: Set<string>;
  hideTrigger?: boolean;
  forcedPosition?: DropdownPosition;
  overlayBackgroundColor?: string;
}

const HarborFilterdropdown: React.FC<KPIFilterDropdownProps> = ({
  childList = [],
  selectedValue = [],
  setSelectedValue,
  placeholder = 'All',
  readOnly = false,
  style = {},
  isOpen: controlledIsOpen,
  onOpenChange,
  singleSelect = false,
  testID,
  autoFocusSearchOnOpen = false,
  sortItems = false,
  disabledIds,
  hideTrigger = false,
  forcedPosition,
  overlayBackgroundColor,
}) => {
  const dc = useDarkColor();
  const dropdownStyles = useDarkStyles(baseDropdownStyles);
  const { t } = useTranslation('harbor');
  const isControlled = controlledIsOpen !== undefined && onOpenChange !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const { width: winWidth, height: winHeight } = useWindowDimensions();

  const [searchText, setSearchText] = useState('');
  const [hoveredItem, setHoveredItem] = useState<{ text: string; x: number; y: number } | null>(null);
  const dropdownRef = useRef<any>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
  });
  const scrollListenerRef = useRef<(() => void) | null>(null);

  const [selectedItems, setSelectedItems] = useState<KPIFilterDropdownItem[]>(selectedValue);

  useEffect(() => {
    setSelectedItems(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    if (hideTrigger && forcedPosition) {
      setDropdownPosition(forcedPosition);
    }
  }, [hideTrigger, forcedPosition]);

  const query = searchText.trim().toLowerCase();
  const filteredItems: KPIFilterDropdownItem[] = useMemo(() => {
    if (!query) {
      const list = [...childList];
      if (sortItems) {
        list.sort((a, b) => a.displayText.localeCompare(b.displayText));
      }
      return list;
    }

    const list = childList.filter(item =>
      item.displayText.toLowerCase().includes(query)
    );

    list.sort((a, b) => {
      const aText = a.displayText.toLowerCase();
      const bText = b.displayText.toLowerCase();

      const aExact = aText === query;
      const bExact = bText === query;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aText.startsWith(query);
      const bStarts = bText.startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      return aText.localeCompare(bText);
    });

    return list;
  }, [childList, query, sortItems]);

  const setOpen = (open: boolean) => {
    if (isControlled) {
      onOpenChange?.(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const recalculatePosition = () => {
    if (hideTrigger && forcedPosition) {
      setDropdownPosition(forcedPosition);
      return;
    }
    if (Platform.OS !== 'web') return;
    if (!dropdownRef.current || typeof dropdownRef.current.getBoundingClientRect !== 'function') return;
    try {
      const APPROX_MENU_HEIGHT = 320;
      const MENU_WIDTH = 240;
      const EDGE_MARGIN = 8;
      const screenH = Dimensions.get('window').height;
      const screenW = Dimensions.get('window').width;
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = screenH - rect.bottom;
      const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
      // Right-align the menu against the trigger's right edge if it would overflow
      const rawLeft = rect.left;
      const left =
        rawLeft + MENU_WIDTH + EDGE_MARGIN > screenW
          ? Math.max(EDGE_MARGIN, rect.right - MENU_WIDTH)
          : rawLeft;
      setDropdownPosition({
        top: flipped ? rect.top - APPROX_MENU_HEIGHT : rect.bottom + 4,
        left,
        width: MENU_WIDTH,
      });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (isOpen) {
      const handler = () => recalculatePosition();
      if (typeof window !== 'undefined') {
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        scrollListenerRef.current = () => {
          window.removeEventListener('scroll', handler, true);
          window.removeEventListener('resize', handler);
        };
      }
    } else if (scrollListenerRef.current) {
      scrollListenerRef.current();
      scrollListenerRef.current = null;
    }

    return () => {
      if (scrollListenerRef.current) {
        scrollListenerRef.current();
        scrollListenerRef.current = null;
      }
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (readOnly || hideTrigger) return;

    if (!isOpen) {
      const APPROX_MENU_HEIGHT = 320;
      const screenH = Dimensions.get('window').height;

      if (Platform.OS !== 'web') {
        if (dropdownRef.current && typeof dropdownRef.current.measureInWindow === 'function') {
          dropdownRef.current.measureInWindow(
            (x: number, y: number, w: number, h: number) => {
              const spaceBelow = screenH - (y + h);
              const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
              setDropdownPosition({
                top: flipped ? y - APPROX_MENU_HEIGHT : y + h + 4,
                left: x,
                width: w,
              });
              setOpen(true);
            }
          );
        } else {
          setOpen(true);
        }
      } else {
        try {
          if (dropdownRef.current && typeof dropdownRef.current.getBoundingClientRect === 'function') {
            const MENU_WIDTH = 240;
            const EDGE_MARGIN = 8;
            const screenW = Dimensions.get('window').width;
            const rect = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = screenH - rect.bottom;
            const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
            // Right-align the menu against the trigger's right edge if it would overflow
            const rawLeft = rect.left;
            const left =
              rawLeft + MENU_WIDTH + EDGE_MARGIN > screenW
                ? Math.max(EDGE_MARGIN, rect.right - MENU_WIDTH)
                : rawLeft;
            setDropdownPosition({
              top: flipped ? rect.top - APPROX_MENU_HEIGHT : rect.bottom + 4,
              left,
              width: MENU_WIDTH,
            });
          }
          setOpen(true);
        } catch {
          setOpen(true);
        }
      }
    } else {
      setOpen(false);
    }
  };

  const isItemDisabled = (item: KPIFilterDropdownItem): boolean => {
    if (!disabledIds || disabledIds.size === 0) return false;
    return disabledIds.has(String(item.id));
  };

  const handleItemToggle = (item: KPIFilterDropdownItem) => {
    if (isItemDisabled(item)) return;

    if (singleSelect) {
      const newSelection = [item];
      setSelectedItems(newSelection);
      setSelectedValue(newSelection);
      handleClose();
      return;
    }

    const isSelected = selectedItems.some(
      s => String(s.id).toLowerCase() === String(item.id).toLowerCase()
    );

    let newSelection: KPIFilterDropdownItem[];
    if (isSelected) {
      newSelection = selectedItems.filter(
        s => String(s.id).toLowerCase() !== String(item.id).toLowerCase()
      );
    } else {
      newSelection = [...selectedItems, item];
    }

    setSelectedItems(newSelection);
    setSelectedValue(newSelection);
  };

  const handleSelectAll = () => {
    if (searchText.trim()) {
      const filteredIds = new Set(filteredItems.map(item => String(item.id).toLowerCase()));
      const existingNonFiltered = selectedItems.filter(
        s => !filteredIds.has(String(s.id).toLowerCase())
      );
      const selectableFiltered = filteredItems.filter(item => !isItemDisabled(item));
      const newSelection = [...existingNonFiltered, ...selectableFiltered];
      setSelectedItems(newSelection);
      setSelectedValue(newSelection);
    } else {
      const selectableItems = childList.filter(item => !isItemDisabled(item));
      setSelectedItems([...selectableItems]);
      setSelectedValue([...selectableItems]);
    }
  };

  const handleClear = () => {
    setSelectedItems([]);
    setSelectedValue([]);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchText('');
    setHoveredItem(null);
  };

  const getDisplayText = (): string => {
    if (selectedItems.length === 0) {
      return placeholder;
    }

    if (singleSelect && selectedItems.length === 1) {
      const item = selectedItems[0];
      if (String(item.id).trim().toLowerCase() === 'all') {
        return 'All';
      }
      return item.displayText;
    }

    const nonAllSelected = selectedItems.filter(
      s => String(s.id).trim().toLowerCase() !== 'all'
    );
    const nonAllOptions = childList.filter(
      s => String(s.id).trim().toLowerCase() !== 'all'
    );

    if (selectedItems.length === 1 && String(selectedItems[0].id).trim().toLowerCase() === 'all') {
      return 'All';
    }
    if (nonAllOptions.length > 0 && nonAllSelected.length >= nonAllOptions.length) {
      return 'All';
    }
    if (selectedItems.length === childList.length && childList.length > 0) {
      return 'All';
    }

    const displayCount = nonAllSelected.length > 0 ? nonAllSelected.length : selectedItems.length;

    if (displayCount === 1) {
      const singleItem = nonAllSelected.length > 0 ? nonAllSelected[0] : selectedItems[0];
      return singleItem.displayText;
    }

    return `${displayCount} selected`;
  };

  const isItemSelected = (item: KPIFilterDropdownItem) =>
    selectedItems.some((s) => String(s.id).toLowerCase() === String(item.id).toLowerCase());

  // Hover handlers that drive the truncation tooltip. Web-only — on native these
  // spread to an empty object so nothing changes.
  const getHoverHandlers = (text: string): any => {
    if (Platform.OS !== 'web') return {};
    return {
      onMouseEnter: (e: any) => {
        const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
        const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
        setHoveredItem({ text, x, y });
      },
      onMouseMove: (e: any) => {
        const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
        const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
        setHoveredItem((prev) => (prev ? { ...prev, x, y } : null));
      },
      onMouseLeave: () => setHoveredItem(null),
    };
  };

  // Final safety clamp: regardless of the position/width supplied by the caller
  // (or measured from the trigger), keep the menu fully inside the viewport.
  const EDGE_MARGIN = 8;
  const desiredWidth = dropdownPosition.width || 240;
  const clampedWidth = Math.min(desiredWidth, Math.max(180, winWidth - EDGE_MARGIN * 2));
  const menuMaxHeight = Math.max(220, Math.round(winHeight * 0.5));
  const clampedLeft = Math.max(
    EDGE_MARGIN,
    Math.min(dropdownPosition.left, winWidth - clampedWidth - EDGE_MARGIN),
  );
  const clampedTop = Math.max(
    EDGE_MARGIN,
    Math.min(dropdownPosition.top, winHeight - menuMaxHeight - EDGE_MARGIN),
  );

  return (
    <View style={[dropdownStyles.container, style]}>
      {!hideTrigger && (
        <TouchableOpacity
          ref={dropdownRef}
          style={[
            dropdownStyles.trigger,
            isOpen && dropdownStyles.triggerActive,
            readOnly && dropdownStyles.triggerDisabled,
          ]}
          onPress={toggleDropdown}
          disabled={readOnly}
          testID={testID}
          activeOpacity={0.7}
          {...getHoverHandlers(getDisplayText())}
        >
          <Text
            style={[
              dropdownStyles.triggerText,
              selectedItems.length === 0 && dropdownStyles.triggerPlaceholder,
            ]}
            numberOfLines={1}
          >
            {getDisplayText()}
          </Text>
          <MaterialIcons
            name={isOpen ? 'expand-less' : 'expand-more'}
            size={20}
            color={readOnly ? '#ADB5BD' : '#495057'}
          />
        </TouchableOpacity>
      )}

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={handleClose}
      >
        <TouchableOpacity
          style={[
            dropdownStyles.overlay,
            { backgroundColor: overlayBackgroundColor ?? (hideTrigger ? 'rgba(0,0,0,0.01)' : 'rgba(0,0,0,0.15)') },
          ]}
          activeOpacity={1}
          onPress={handleClose}
        >
          <TouchableOpacity
            activeOpacity={1}
            style={[
              dropdownStyles.menu,
              {
                top: clampedTop,
                left: clampedLeft,
                width: clampedWidth,
                maxHeight: menuMaxHeight,
              },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={dropdownStyles.stickyHeader}>
              <View style={dropdownStyles.searchRow}>
                <MaterialIcons name="search" size={18} color={dc("#7C8086")} />
                <TextInput
                  style={[
                    dropdownStyles.searchInput,
                    Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null,
                  ]}
                  placeholder={t('common.search')}
                  placeholderTextColor={dc("#9AA1AB")}
                  value={searchText}
                  onChangeText={setSearchText}
                  autoFocus={autoFocusSearchOnOpen}
                />
                {searchText ? (
                  <TouchableOpacity onPress={() => setSearchText('')}>
                    <MaterialIcons name="close" size={16} color={dc("#6D737C")} />
                  </TouchableOpacity>
                ) : null}
              </View>

              <View style={dropdownStyles.actionsRow}>
                <TouchableOpacity onPress={handleSelectAll} style={dropdownStyles.actionButton}>
                  <Text style={dropdownStyles.actionText}>Select All</Text>
                </TouchableOpacity>
                <View style={dropdownStyles.actionDivider} />
                <TouchableOpacity onPress={handleClear} style={dropdownStyles.actionButton}>
                  <Text style={dropdownStyles.actionText}>Clear</Text>
                </TouchableOpacity>
              </View>
            </View>

            {Platform.OS === 'web' ? (
              <View style={dropdownStyles.list} key={`list-${searchText.trim().toLowerCase()}`}>
                {filteredItems.length === 0 ? (
                  <Text style={dropdownStyles.emptyText}>{t('common.no_results_found')}</Text>
                ) : (
                  filteredItems.map((item) => {
                    const selected = isItemSelected(item);
                    const disabled = isItemDisabled(item);
                    return (
                      <TouchableOpacity
                        key={String(item.id)}
                        style={[
                          dropdownStyles.item,
                          selected && !disabled && dropdownStyles.itemSelected,
                          disabled && dropdownStyles.itemDisabled,
                        ]}
                        onPress={() => handleItemToggle(item)}
                        activeOpacity={disabled ? 1 : 0.7}
                        {...getHoverHandlers(item.displayText)}
                      >
                        <View
                          style={[
                            dropdownStyles.checkbox,
                            selected && !disabled && dropdownStyles.checkboxChecked,
                            disabled && dropdownStyles.checkboxDisabled,
                          ]}
                        >
                          {selected && !disabled && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
                        </View>
                        <Text style={[dropdownStyles.itemText, disabled && dropdownStyles.itemTextDisabled]} numberOfLines={1}>
                          {item.displayText}
                        </Text>
                        {disabled && <MaterialIcons name="lock" size={12} color={dc("#ADB5BD")} />}
                      </TouchableOpacity>
                    );
                  })
                )}
              </View>
            ) : (
              <ScrollView
                style={dropdownStyles.listNative}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {filteredItems.length === 0 ? (
                  <Text style={dropdownStyles.emptyText}>{t('common.no_results_found')}</Text>
                ) : (
                  filteredItems.map((item) => {
                    const selected = isItemSelected(item);
                    const disabled = isItemDisabled(item);
                    return (
                      <TouchableOpacity
                        key={String(item.id)}
                        style={[
                          dropdownStyles.item,
                          selected && !disabled && dropdownStyles.itemSelected,
                          disabled && dropdownStyles.itemDisabled,
                        ]}
                        onPress={() => handleItemToggle(item)}
                        activeOpacity={disabled ? 1 : 0.7}
                      >
                        <View
                          style={[
                            dropdownStyles.checkbox,
                            selected && !disabled && dropdownStyles.checkboxChecked,
                            disabled && dropdownStyles.checkboxDisabled,
                          ]}
                        >
                          {selected && !disabled && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
                        </View>
                        <Text style={[dropdownStyles.itemText, disabled && dropdownStyles.itemTextDisabled]} numberOfLines={1}>
                          {item.displayText}
                        </Text>
                        {disabled && <MaterialIcons name="lock" size={12} color={dc("#ADB5BD")} />}
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>
            )}

            <View style={dropdownStyles.footer}>
              <Text style={dropdownStyles.footerText}>
                {selectedItems.length} of {childList.length} selected
              </Text>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Tooltip for truncated trigger/option labels. Portalled to document.body,
          so a single instance here covers both the closed trigger and the open menu. */}
      <ItemTooltip
        visible={hoveredItem !== null}
        position={hoveredItem}
        text={hoveredItem?.text ?? ''}
      />
    </View>
  );
};

const baseDropdownStyles = StyleSheet.create({
  container: {
    width: '100%',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    minHeight: 40,
  },
  triggerActive: {
    borderColor: '#0B5FC6',
  },
  triggerDisabled: {
    opacity: 0.5,
    backgroundColor: '#F8F9FA',
  },
  triggerText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
    marginRight: 8,
  },
  triggerPlaceholder: {
    color: '#7C8086',
  },
  overlay: {
    flex: 1,
  },
  menu: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    // maxHeight is applied inline (clamped to the live viewport height)
    overflow: 'hidden',
    zIndex: 9999,
  },
  stickyHeader: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F3F5',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
    paddingVertical: 4,
    width: '100%',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  actionButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  actionText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#0B5FC6',
  },
  actionDivider: {
    width: 1,
    height: 16,
    backgroundColor: '#DEE2E6',
    marginHorizontal: 4,
  },
  list: {
    ...(Platform.OS === 'web' ? { overflowY: 'auto' } : {}),
    flexShrink: 1,
    width: '100%',
  } as any,
  listNative: {
    flexShrink: 1,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 10,
  },
  itemSelected: {
    backgroundColor: '#F0F6FF',
  },
  itemDisabled: {
    opacity: 0.5,
    backgroundColor: '#F8F9FA',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#ADB5BD',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: '#0B5FC6',
    borderColor: '#0B5FC6',
  },
  checkboxDisabled: {
    borderColor: '#DEE2E6',
    backgroundColor: '#F1F3F5',
  },
  itemText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#343A40',
  },
  itemTextDisabled: {
    color: '#ADB5BD',
  },
  emptyText: {
    padding: 16,
    textAlign: 'center',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#6C757D',
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: '#E9ECEF',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F8F9FA',
  },
  footerText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6B6E72',
    textAlign: 'center',
  },
});

export default HarborFilterdropdown;
